import { Injectable } from '@angular/core';

export type ErpAccessScope = 'entire_erp' | 'transportation_only';

export interface ErpAccessMenuItem {
  name?: string;
  linkto?: string;
  submenu?: ErpAccessMenuItem[];
  children?: ErpAccessMenuItem[];
  external?: boolean;
  roles?: string[];
  featureKey?: string;
  isOpen?: boolean;
}

@Injectable({ providedIn: 'root' })
export class ErpAccessService {
  private static readonly STORAGE_KEY = 'erpAccessScope';
  private static readonly DEFAULT_SCOPE: ErpAccessScope = 'entire_erp';

  private readonly allowedTransportTopLevelNames = new Set(['Dashboard', 'Admin', 'HRMS', 'Transport']);
  private readonly allowedAdminModules = new Set(['Dashboard', 'Student Management', 'Master Data']);
  private readonly allowedStudentManagementModules = new Set(['Students', 'Parents']);
  private readonly allowedHrmsModules = new Set(['My Profile', 'Directory']);
  // Tabs available inside a Directory employee profile, including their child routes.
  private readonly employeeProfilePages: Record<string, readonly string[]> = {
    personal: [''],
    team: [''],
    education: [''],
    family: [''],
    document: ['', 'document', 'certification', 'work'],
    'work-week': [''],
    'pay-slip': ['', 'pay-slip', 'salary-structure', 'bank-account'],
    attendance: ['', 'attendance', 'attendance/attendance', 'attendance/monthly-log',
      'automation-logs', 'automation-logs/automation-logs', 'automation-logs/comp-off', 'rules'],
  };

  getScope(): ErpAccessScope {
    const raw = this.readPersistedScope();
    return this.normalize(raw) ?? ErpAccessService.DEFAULT_SCOPE;
  }

  setScope(scope: string | null | undefined): void {
    const normalized = this.normalize(scope);
    const value = normalized ?? ErpAccessService.DEFAULT_SCOPE;
    localStorage.setItem(ErpAccessService.STORAGE_KEY, value);
  }

  isTransportationOnly(): boolean {
    return this.getScope() === 'transportation_only';
  }

  isAllowedUrl(url: string | null | undefined): boolean {
    if (!url) return false;
    const normalized = this.normalizeUrl(url);
    if (!normalized) return false;

    if (!this.isTransportationOnly()) {
      return true;
    }

    // Allow Master Data and its pages without admitting similarly named modules.
    if (normalized === '/nora/admin/master' || normalized.startsWith('/nora/admin/master/')) {
      return true;
    }

    // Keep employee-profile navigation available without opening the HRMS module broadly.
    const employeePage = normalized.match(/^\/nora\/hrms\/impersonate\/([^/]+)\/employee\/[^/]+(?:\/(.*))?$/);
    if (employeePage) {
      const childPath = (employeePage[2] ?? '').replace(/\/$/, '');
      return this.employeeProfilePages[employeePage[1]]?.includes(childPath) === true;
    }

    const allowedRoutes = new Set<string>([
      '/nora/admin/dashboard',
      '/nora/admin/student-management/student',
      '/nora/admin/student-management/parent',
      '/nora/admin/student-management/inactive-students',
      '/nora/admin/class-management',
      '/nora/hrms/profile',
      '/nora/hrms/directory',
      '/nora/transport/dashboard',
      '/nora/transport/live-trackinghome',
      '/nora/transport/bus-routes',
      '/nora/transport/fleet-management',
      '/nora/transport/pickup-points',
      '/nora/transport/driver-management',
      '/nora/transport/caretaker-management',
      '/nora/transport/student-directory',
      '/nora/transport/fee-management',
      '/nora/transport/trip-history',
      '/nora/transport/trip-logs',
      '/nora/transport/route-analytics',
      '/nora/transport/vehicle-reports',
      '/nora/transport/attendance',
      '/nora/transport/driver-attendance',
      '/nora/transport/caretaker-attendance',
      '/nora/transport/sms-communication',
      '/nora/transport/notifications',
      '/nora/dashboard',
      '/nora'
    ]);

    if (allowedRoutes.has(normalized)) {
      return true;
    }

    const allowedPrefixes = [
      '/nora/admin/dashboard',
      '/nora/admin/student-management/student',
      '/nora/admin/student-management/parent',
      '/nora/admin/class-management',
      '/nora/hrms/profile',
      '/nora/hrms/directory',
      '/nora/transport/'
    ];

    return allowedPrefixes.some(prefix => normalized.startsWith(prefix));
  }

  getFallbackUrl(): string {
    return this.isTransportationOnly() ? '/nora/admin/dashboard' : '/nora/dashboard';
  }

  filterMenu<T extends ErpAccessMenuItem>(menu: T[]): T[] {
    if (!this.isTransportationOnly()) {
      return menu;
    }

    const topLevel = menu.filter(item => item && this.allowedTransportTopLevelNames.has(item.name ?? ''));
    const dashboardItem = topLevel.find(item => item.name === 'Dashboard');
    const adminItem = topLevel.find(item => item.name === 'Admin') as T | undefined;
    const hrmsItem = topLevel.find(item => item.name === 'HRMS') as T | undefined;
    const transportItem = topLevel.find(item => item.name === 'Transport') as T | undefined;

    const next: T[] = [];

    if (dashboardItem) next.push(dashboardItem);

    if (adminItem) {
      const adminMenu = {
        ...adminItem,
        isOpen: false,
        submenu: [
          { name: 'Dashboard', linkto: '/nora/admin/dashboard', isOpen: false },
          {
            name: 'Student Management',
            linkto: '/nora/admin/student-management',
            isOpen: false,
            submenu: [
              { name: 'Students', linkto: '/nora/admin/student-management/student', isOpen: false },
              { name: 'Parents', linkto: '/nora/admin/student-management/parent', isOpen: false },
            ],
          },
          { name: 'Master Data', linkto: '/nora/admin/class-management', isOpen: false },
        ],
      } as T;
      next.push(adminMenu);
    }

    if (hrmsItem) {
      const hrmsMenu = {
        ...hrmsItem,
        isOpen: false,
        submenu: [
          { name: 'My Profile', linkto: '/nora/hrms/profile', isOpen: false },
          { name: 'Directory', linkto: '/nora/hrms/directory', isOpen: false },
        ],
      } as T;
      next.push(hrmsMenu);
    }

    if (transportItem) {
      next.push({
        ...transportItem,
        submenu: transportItem.submenu ?? [],
      } as T);
    }

    return next;
  }

  private normalize(scope: string | null | undefined): ErpAccessScope | null {
    const value = typeof scope === 'string' ? scope.trim() : '';
    if (!value) return null;
    if (value === 'entire_erp') return 'entire_erp';
    if (value === 'transportation_only') return 'transportation_only';
    return ErpAccessService.DEFAULT_SCOPE;
  }

  private readPersistedScope(): string | null {
    try {
      return localStorage.getItem(ErpAccessService.STORAGE_KEY);
    } catch {
      return null;
    }
  }

  private normalizeUrl(url: string): string {
    if (!url) return '';
    const trimmed = url.split('?')[0].split('#')[0].trim();
    if (!trimmed || trimmed === '/') return '/nora';
    return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  }
}
